import { RETAILER_DROPSHIPPING_ACTIVATE } from '@marketplace/graphql/dropshipping.js'
import { unlocalizeRouteName } from '@marketplace/helpers/router.js'
import { localeUrl } from '@marketplace/helpers/url.js'

const supplierEnabledRoutes = new Set([
    'ds', // Homepage
    'ds-listings',
    'ds-marketplace',
    'ds-search',
    'ds-store-slug',
    'ds-store-slug-about',
    'ds-store-slug-collection',
    'ds-store-slug-listings-listingId',
])

/**
 * Get the locale-based request access URL for dropshipping.
 * Returns URLs in the format:
 * - EN: https://dropshipping.orderchamp.com/request-access
 * - NL, DE, FR: https://dropshipping.orderchamp.com/{locale}/request-access
 * - Other langs: https://dropshipping.orderchamp.com/request-access
 * @param {string} locale
 * @returns {string}
 */
function getRequestAccessUrl(locale) {
    const baseUrl = 'https://dropshipping.orderchamp.com'
    const path = '/request-access'
    const supportedLocales = ['nl', 'de', 'fr']

    if (supportedLocales.includes(locale?.toLowerCase())) {
        return `${baseUrl}/${locale.toLowerCase()}${path}`
    }

    return `${baseUrl}${path}`
}

/**
 * Check if the user has the dropshipping feature and the status of the dropshipping signup.
 * @param {object} root0
 * @param {object} root0.store
 * @param {Function} root0.redirect
 * @param {object} root0.route
 * @param {object} root0.app
 * @param {object} root0.$helpers
 * @param {Function} root0.error
 * @returns {void}
 */
export default async function ({
    store,
    route,
    redirect,
    app,
    $helpers,
    error,
}) {
    const routeName = unlocalizeRouteName(route.name)
    const isLoggedIn = $helpers.isLoggedIn

    if (!isLoggedIn) {
        const currentLocale = app.i18n.locale
        const redirectUrl = getRequestAccessUrl(currentLocale)
        return redirect(redirectUrl)
    }

    const hasDropshippingFeature =
        store.getters['feature/hasDropshippingFeature']
    const hasOneBrandFeature = store.getters['feature/hasOneBrandFeature']
    const dsStorefronts = store.getters['ds/dsStorefronts']
    const isSupplier = store.getters['global/isSupplier']
    let dropshipping = store.getters['global/account']?.dropshipping
    const dsSignupPath = '/ds/signup'
    const dsSignupErrorPath = '/ds/signup/error'

    if (!hasDropshippingFeature) {
        redirect(localeUrl('/'))
        return
    }

    if (
        hasOneBrandFeature &&
        (routeName === 'ds' || !routeName.startsWith('ds'))
    ) {
        return redirect(app.localePath({ name: 'ds-import-list' }))
    }
    const hasStorefronts =
        Array.isArray(dsStorefronts) && dsStorefronts.length > 0

    if (
        hasOneBrandFeature &&
        hasStorefronts &&
        routeName === 'ds-marketplace'
    ) {
        return redirect(app.localePath({ name: 'ds-listings' }))
    }

    if (isSupplier) {
        if (
            supplierEnabledRoutes.has(routeName) &&
            dropshipping?.status === 'APPROVED'
        ) {
            return
        }

        error({ statusCode: 401 })
        return
    }

    if (!dropshipping) {
        try {
            const { data } = await app.apolloProvider.defaultClient.mutate({
                mutation: RETAILER_DROPSHIPPING_ACTIVATE,
                variables: { input: {} },
            })

            dropshipping =
                data?.retailerDropshippingActivate?.account?.dropshipping ??
                null

            if (dropshipping) {
                store.commit('global/updateUserData', { dropshipping })
            }
        } catch (e) {}
    }

    if (
        dropshipping?.status === 'REJECTED' &&
        !route.path.includes(dsSignupErrorPath)
    ) {
        return redirect(app.localePath({ name: 'ds-signup-error' }))
    }

    if (
        dropshipping?.status === 'APPROVED' &&
        route.path.includes(dsSignupPath) &&
        !route.path.includes(dsSignupErrorPath)
    ) {
        return redirect(app.localePath({ name: 'ds' }))
    }
}
